import React, {memo, useLayoutEffect, useState} from 'react';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import Box from '@material-ui/core/Box';
import {InputLabel} from '@material-ui/core';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import useLocalization from '../../hooks/useLocalization';
import BulkExportButton from './components/BulkExportButton';
import GameSelect, {Category} from '../../components/game-select/GameSelect';
import TotalActiveUsersWidget from '../../components/v2/sections/fortnite-games/total-active-users/TotalActiveUsersWidget';
import AveragePlayTimeWidget from '../../components/v2/sections/fortnite-games/average-play-time/AveragePlayTimeWidget';
import ReturnPlayersWidget from '../../components/v2/sections/fortnite-games/return-players/ReturnPlayersWidget';
import {GlobalDatePicker, getTimeRangeDates, Sidebar} from '../../components';
import TotalLifetimePlaysWidget from '../../components/v2/sections/fortnite-games/total-lifetime-plays/TotalLifetimePlaysWidget';

import useGamesData from './useGamesData';
import {useRangePickerData} from '../../components/common/date-range-picker';

import {TimeScale, TimeRange} from '../../types';

import {
    WidgetsContainer,
    SelectWrapper,
    ActionContainer,
    ControlWrapper,
    ChartContainer,
    InsightsSpacer
} from './FortniteGames.styles';
import {useFnGamesTour} from './FnGamesTourProvider';

const CODES = {
    help: 'epic.affiliate.fortnite_games.active_users.help',
    helpLink: 'epic.affiliate.fortnite_games.active_users.help_link',
    selectLabel: 'epic.affiliate.fortnite_games.game_select.label',
    timeRangeLabel: 'epic.affiliate.fortnite_games.time_range.label',
    groupLabel: 'epic.affiliate.fortnite_games.group.label'
};

const WIDGETS = [
    TotalActiveUsersWidget,
    AveragePlayTimeWidget,
    ReturnPlayersWidget,
    TotalLifetimePlaysWidget
];

const Insights = () => {
    const getMessage = useLocalization();
    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));

    const {onShow} = useFnGamesTour();
    const [selectedGames, setSelectedGames] = useState({
        category: Category.Top10TrendingOptions,
        options: []
    });
    const [globalTimeScale, setGlobalTimeScale] = useState<TimeScale>(TimeScale.LIVE);

    const initialTimeRange = getTimeRangeDates(TimeRange.FORTNIGHT);
    const {startDate, endDate, selectedDate, setCalendar} = useRangePickerData(initialTimeRange);

    const {
        data: {
            selectOptions,
            recent10GamesOptions,
            top10GamesOptions,
            top10TrendingOptions,
            codeNamesMap
        },
        loading: gamesLoading
    } = useGamesData();

    // Set Top 10 trending by default
    useLayoutEffect(() => {
        setSelectedGames({
            category: Category.Top10GamesOptions,
            options: top10GamesOptions
        });
    }, [top10TrendingOptions]);

    const handleSelectGames = (category, options) => {
        setSelectedGames({category, options});
    };

    return (
        <>
            {/* Controls section */}
            <ActionContainer>
                <Box width="100%">
                    <ControlWrapper>
                        <SelectWrapper>
                            {isFmdBreakpoint ? (
                                <InputLabel>{getMessage(CODES.selectLabel)}</InputLabel>
                            ) : (
                                <InputLabel>{getMessage(CODES.groupLabel)}</InputLabel>
                            )}
                            <GameSelect
                                selectOptions={selectOptions}
                                recent10GamesOptions={recent10GamesOptions}
                                top10GamesOptions={top10GamesOptions}
                                top10TrendingOptions={top10TrendingOptions}
                                value={selectedGames}
                                loading={gamesLoading}
                                onChange={handleSelectGames}
                            />
                        </SelectWrapper>
                        <InsightsSpacer />
                        <SelectWrapper>
                            {isFmdBreakpoint && (
                                <InputLabel>{getMessage(CODES.timeRangeLabel)}</InputLabel>
                            )}
                            <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                                <GlobalDatePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    selectedDate={selectedDate}
                                    onRangeChange={setCalendar}
                                    onTimeScaleChange={setGlobalTimeScale}
                                />
                            </MuiPickersUtilsProvider>
                        </SelectWrapper>
                        <InsightsSpacer />
                    </ControlWrapper>

                    <Typography variant="body1" color="textSecondary">
                        {`${getMessage(CODES.help)} `}

                        <Link underline="none" color="primary" onClick={onShow}>
                            {getMessage(CODES.helpLink)}
                        </Link>
                    </Typography>
                </Box>

                <BulkExportButton />
            </ActionContainer>

            {/* Widgets section */}
            <WidgetsContainer>
                <ChartContainer>
                    {WIDGETS.map((Widget, index) => (
                        <Widget
                            key={index}
                            globalTimeScale={globalTimeScale}
                            selectedGames={selectedGames.options}
                            selectedDate={selectedDate}
                            startDate={startDate}
                            endDate={endDate}
                            gamesList={selectOptions}
                            keysMap={codeNamesMap}
                            gamesLoading={gamesLoading}
                        />
                    ))}
                </ChartContainer>
                <Sidebar />
            </WidgetsContainer>
        </>
    );
};

export default memo(Insights);
